/**
 * The annual UMA value (LFPIORPI art. 17 writes its lines as a number of daily UMA).
 *
 * The designated compliance officer or a super_admin enters the daily value INEGI publishes in the
 * Diario Oficial de la Federacion each year; it takes effect on February 1. One person enters it, so the
 * entry names its source (the publication date), every value stays in the history, and a value that
 * differs from the one on file by more than 10% asks for a second step before it is saved.
 */
import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import { calendarDate } from '../../components/client/enteringFund.helpers';
import umaValuesService, {
  type LargeChange,
  type UmaOverview,
  type UmaValue as UmaValueRow,
} from '../../services/umaValues.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { canSend, largeChangeOf, pesos, toEntry, type UmaForm } from '../../lib/umaValues';
import { colors } from '../../styles/theme';

const INPUT_STYLE = { border: `1px solid ${colors.border}` };

function Field({
  id,
  label,
  hint,
  children,
}: Readonly<{ id: string; label: string; hint?: string; children: React.ReactNode }>): JSX.Element {
  return (
    <div>
      <label className="block text-xs mb-1" htmlFor={id} style={{ color: colors.muted }}>
        {label}
      </label>
      {children}
      {hint && <p className="text-xs mt-1" style={{ color: colors.muted }}>{hint}</p>}
    </div>
  );
}

export function UmaEntryForm({
  form,
  busy,
  onChange,
  onSubmit,
}: Readonly<{
  form: UmaForm;
  busy: boolean;
  onChange: (next: UmaForm) => void;
  onSubmit: () => void;
}>): JSX.Element {
  return (
    <form
      className="grid gap-4 sm:grid-cols-2"
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
    >
      <Field id="uma-value" label="Daily UMA value (pesos)" hint="The daily value, not the monthly or the annual one.">
        <input
          id="uma-value"
          type="text"
          inputMode="decimal"
          value={form.value}
          onChange={(e) => onChange({ ...form, value: e.target.value })}
          className="w-full px-3 py-2 rounded-lg text-sm"
          style={INPUT_STYLE}
        />
      </Field>
      <Field id="uma-effective" label="Takes effect on" hint="The UMA changes every February 1.">
        <input
          id="uma-effective"
          type="date"
          value={form.effectiveFrom}
          onChange={(e) => onChange({ ...form, effectiveFrom: e.target.value })}
          className="w-full px-3 py-2 rounded-lg text-sm"
          style={INPUT_STYLE}
        />
      </Field>
      <Field id="uma-dof" label="Date of the publication in the Diario Oficial" hint="The source of the value.">
        <input
          id="uma-dof"
          type="date"
          value={form.dofPublicationDate}
          onChange={(e) => onChange({ ...form, dofPublicationDate: e.target.value })}
          className="w-full px-3 py-2 rounded-lg text-sm"
          style={INPUT_STYLE}
        />
      </Field>
      <Field id="uma-year" label="Year it applies to (optional)">
        <input
          id="uma-year"
          type="text"
          inputMode="numeric"
          maxLength={4}
          value={form.appliesToYear}
          onChange={(e) => onChange({ ...form, appliesToYear: e.target.value })}
          className="w-full px-3 py-2 rounded-lg text-sm"
          style={INPUT_STYLE}
        />
      </Field>
      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={busy || !canSend(form)}
          className="text-sm px-3 py-2 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: colors.gold, color: colors.navy }}
        >
          Save the value
        </button>
      </div>
    </form>
  );
}

export function ConfirmLargeChange({
  change,
  newValue,
  busy,
  onConfirm,
  onBack,
}: Readonly<{
  change: LargeChange;
  newValue: string;
  busy: boolean;
  onConfirm: () => void;
  onBack: () => void;
}>): JSX.Element {
  return (
    <div role="alertdialog" aria-labelledby="uma-confirm-title" className="rounded-lg p-4" style={{ border: `1px solid ${colors.border}` }}>
      <p id="uma-confirm-title" className="text-sm font-semibold" style={{ color: colors.ink }}>
        The new value differs by {change.difference_percent}% from the current one. Confirm?
      </p>
      <p className="text-sm mt-2" style={{ color: colors.inkSoft }}>
        Current value: {pesos(change.current_value)}. New value: {pesos(newValue)}.
      </p>
      <div className="flex gap-3 mt-4">
        <button
          type="button"
          disabled={busy}
          onClick={onConfirm}
          className="text-sm px-3 py-2 rounded-lg font-semibold disabled:opacity-50"
          style={{ background: colors.gold, color: colors.navy }}
        >
          Confirm and save
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onBack}
          className="text-sm px-3 py-2 rounded-lg"
          style={INPUT_STYLE}
        >
          Go back
        </button>
      </div>
    </div>
  );
}

export function UmaHistory({
  rows,
  inForceId,
}: Readonly<{ rows: readonly UmaValueRow[]; inForceId: string | null }>): JSX.Element {
  if (rows.length === 0) {
    return <p className="text-sm" style={{ color: colors.muted }}>No value has been entered yet.</p>;
  }
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left" style={{ color: colors.muted }}>
          <th className="py-2 pr-3 font-normal">Takes effect on</th>
          <th className="py-2 pr-3 font-normal">Daily value</th>
          <th className="py-2 pr-3 font-normal">Published in the Diario Oficial</th>
          <th className="py-2 pr-3 font-normal">Entered by</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id} style={{ borderTop: `1px solid ${colors.border}` }}>
            <td className="py-2 pr-3">
              {calendarDate(row.effective_from)}
              {row.id === inForceId && <span className="ml-2 text-xs" style={{ color: colors.muted }}>In force today</span>}
            </td>
            <td className="py-2 pr-3">{pesos(row.daily_value_mxn)}</td>
            <td className="py-2 pr-3">{calendarDate(row.dof_publication_date)}</td>
            <td className="py-2 pr-3"><span translate="no">{row.entered_by_name ?? '—'}</span></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function emptyForm(overview: UmaOverview | null): UmaForm {
  return {
    value: '',
    effectiveFrom: overview?.proposed_effective_from ?? '',
    dofPublicationDate: '',
    appliesToYear: overview ? overview.proposed_effective_from.slice(0, 4) : '',
  };
}

export default function UmaValue(): JSX.Element {
  const [overview, setOverview] = useState<UmaOverview | null>(null);
  const [form, setForm] = useState<UmaForm>(emptyForm(null));
  const [change, setChange] = useState<LargeChange | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const reload = useCallback(async () => {
    try {
      const next = await umaValuesService.overview();
      setOverview(next);
      setForm(emptyForm(next));
      setError(null);
    } catch {
      setError('Only the compliance officer and super admins can open this page.');
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  const send = async (confirmed: boolean): Promise<void> => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await umaValuesService.enter(toEntry(form, confirmed));
      setChange(null);
      setSaved(true);
      await reload();
    } catch (e: unknown) {
      const asked = largeChangeOf(e);
      if (asked) setChange(asked);
      else setError(getApiErrorMessage(e, 'Could not save the value.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="UMA value"
        subtitle="The daily UMA value the thresholds written in UMA are converted with. Enter it when it is published."
      />
      {overview && (
        <p className="mb-6 text-sm" style={{ color: colors.inkSoft }}>
          {overview.in_force
            ? <>In force today: {pesos(overview.in_force.daily_value_mxn)}, since {calendarDate(overview.in_force.effective_from)}.</>
            : 'No value is in force today.'}
        </p>
      )}
      {error && <p className="mb-4 text-sm" role="alert" style={{ color: colors.danger }}>{error}</p>}
      {saved && <output className="mb-4 block text-sm" style={{ color: colors.inkSoft }}>The value was saved.</output>}
      {overview && (change
        ? (
          <ConfirmLargeChange
            change={change}
            newValue={form.value.trim()}
            busy={busy}
            onConfirm={() => { void send(true); }}
            onBack={() => setChange(null)}
          />
        )
        : <UmaEntryForm form={form} busy={busy} onChange={setForm} onSubmit={() => { void send(false); }} />)}
      {overview && (
        <div className="mt-10">
          <h2 className="text-base font-semibold mb-3" style={{ color: colors.ink }}>History</h2>
          <UmaHistory rows={overview.history} inForceId={overview.in_force?.id ?? null} />
        </div>
      )}
    </div>
  );
}
